import { ComplexQueryOutline } from './outline/ComplexQueryOutline';
import { LiveQueryOutline } from './outline/LiveQueryOutline';
import { LiveDataQueryResults } from './outline/LiveDataQueryResults';
import { SimpleQueryOutline } from './outline/SimpleQueryOutline';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { QueryExecutorUrlProvider } from '@app/analytics/metric-widget/query/query-executor-url-provider';
import { QueryResult } from '@app/analytics/metric-widget/data-objects/query-result';
import { IQueryExecutor } from '@app/analytics/metric-widget/query/query-executor';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { NodeStatusDTO } from '@app/analytics/metric-widget/data-objects/node-status';
import { Pair } from '@app/analytics/metric-widget/data-objects/pair';

@Injectable({
  providedIn: 'root'
})
export class QueryExecutorService implements IQueryExecutor {
  private canceller;
  private nodeDataCanceller;

  private createErrorQueryResult(): QueryResult<number> {
    return {
      values: [],
      max: null as any,
      suffix: ''
    };
  }

  constructor(protected $http: HttpClient, private configuration: QueryExecutorUrlProvider) {}

  doSimpleQuery(outline: SimpleQueryOutline): Observable<QueryResult<number>> {
    throw new Error('Method not implemented.');
  }

  public doComplexQuery(outline: ComplexQueryOutline): Observable<QueryResult<number>> {
    return this.$http.post<QueryResult<number>>(this.configuration.complexQueryUrl, outline);
  }

  public doLiveQuery(outline: LiveQueryOutline): Observable<QueryResult<number>> {
    return this.$http.post<QueryResult<number>>(this.configuration.liveQueryUrl, outline);
  }

  public cancelLiveQuery(): void {
    this.canceller?.resolve();
  }

  public cancelNodeDataQuery(): void {
    this.nodeDataCanceller?.resolve();
  }

  doLiveDataQuery(outline: LiveQueryOutline): Observable<LiveDataQueryResults> {
    const light$ = this.$http.post<NodeStatusDTO[]>(this.configuration.liveLightLevelQueryUrl, outline).pipe(
      map((list) => this.mapNodeStatusToLightQueryResult(list)),
      catchError((err) => {
        console.error('Light level query failed', err);
        return of(this.createErrorQueryResult()); // returns QueryResult<number>
      })
    );

    const presence$ = this.$http.post<QueryResult<number>>(this.configuration.livePresenceLevelQueryUrl, outline).pipe(
      catchError((err) => {
        console.error('Presence level query failed', err);
        return of(this.createErrorQueryResult());
      })
    );

    return forkJoin([light$, presence$]).pipe(
      map(([lightResult, presenceResult]) => new LiveDataQueryResults(lightResult, presenceResult))
    );
  }

  private mapNodeStatusToLightQueryResult(list: NodeStatusDTO[]): QueryResult<number> {
    const values: Pair<number, number>[] = (list ?? [])
      .filter(
        (s) =>
          s.address !== null &&
          s.address !== undefined &&
          s.lightOutputLevel !== null &&
          s.lightOutputLevel !== undefined
      )
      .map((s) => Pair.of<number, number>(s.address, s.lightOutputLevel));

    // For light level: max = 100, suffix = '%'
    return new QueryResult<number>(values, 100, '%');
  }
}
